import React from 'react';
import theme from 'theme';

// tools for hsl(a) color value generation
const [hue, sat, lig] = theme.palette.primary.light
  .match(/\d+/g)
  .map((item) => parseInt(item, 10));

const opa = 40;
const hueShift = -35;
//const hueShift = -32;

export const hsl = (h, s, l) => `hsl(${h}, ${s}%, ${l}%)`;
export const hsla = (h, s, l, a) =>
  `hsla(${h}, ${s}%, ${l}%, ${a}%)`;

const createColorVariant = ({ num, transparent }) => {
  const customHue = num ? hue + hueShift * num : hue;
  return transparent
    ? hsla(customHue, sat, lig, opa)
    : hsl(customHue, sat, lig);
};

// Accessible color palette
const paletteColours = [
  '#DE1C1C', // Hankkeen aloitus
  '#AAAAAA', // Lausuntokierroksen asiakirjat(!) - ei tietoa mitä ovat
  '#064EEA', // Lausuntokierroksen lausunnot
  '#FBB901', // Lausuntokierroksen muut asiakirjat
  '#01953F', // Lakialoitteet
  '#F37500', // Kansalaisaloitteet
  '#8724B2', // Hallituksen esitykset
  '#7A4419', // Asiantuntijalausunnot
  '#4CC9F0', // Valiokunta-asiakirjat
  '#FFFFFF', // Eduskuntakeskustelut
  '#FF85AC', // Eduskunnan vastaukset
  '#000000', // Lakipäätökset
  '#9FFFC6', // Extra 2
];
const accessibleComponents = [
  () => { return (<rect width="10" height="10" y="-5" fill="#DE1C1C" stroke="black"/>) },// Hankkeen aloitus
  () => { return (<circle cx="0" cy="0" r="5" fill="#AAAAAA" stroke="black" />)}, // Lausuntokierroksen asiakirjat(!) - ei tietoa mitä ovat
  () => { return (<circle cx="0" cy="0" r="5" fill="#064EEA" stroke="black" />)}, // Lausuntokierroksen lausunnot
  () => { return (<polygon points="6,-5 12,5 0,5" fill="#FBB901" stroke="black"/>)}, // Lausuntokierroksen muut asiakirjat
  () => { return (<polygon points="3,0 7,0 10,3 10,7 7,10 3,10 0,7 0,3" fill="#01953F" stroke="black"/>)}, // Lakialoitteet
  () => { return (<polygon points="5,0 6.2,3.5 10,3.5 7,6 8.2,10 5,7.5 1.8,10 3,6 0,3.5 3.8,3.5" fill="#F37500" stroke="black" />) },  // Kansalaisaloitteet
  () => { return (<rect width="12" height="8" y="-5" fill="#8724B2" stroke="black"/>) }, // Hallituksen esitykset
  () => { return (<polygon points="8,-9 14,-4 11,5 3,5 0,-4" fill="#7A4419" stroke="black"/>) }, // Asiantuntijalausunnot
  () => { return (<path d="M-4,-5 A8,8 0 0,0 12,-5 Z" fill="#4CC9F0" stroke="black"/>) }, // Valiokunta-asiakirjat
  () => { return (<path d="M3,-5 H7 V-2 H10 V2 H7 V5 H3 V2 H0 V-2 H3 Z" fill="white" stroke="black"/>)}, // Eduskuntakeskustelut
  () => { return (<polygon points="6,5 12,-5 0,-5" fill="#FF85AC" stroke="black"/>)}, // Eduskunnan vastaukset
  () => { return (<polygon points="6,-5 12,2 6,9 0,2" fill="#000000" stroke="black" />)}, // Lakipäätökset
];

// Generate hsl color hue variants
// colorVariant(0) is primary color light
// and colorVariant(n) is n:th variant
// To be used in sequence ie. 0, 1, 2...
export const colorVariant = (num) =>
  paletteColours[num % 13]
//  createColorVariant({ num });

// Generate transparent hsl color hue variants
// as above but with 40% opacity
export const colorVariantTp = (num) =>
  createColorVariant({ num, transparent: true });

export const componentVariant = (num) =>
  accessibleComponents[num % 13];

export const accessibleComponentsLegend = (num, filled = true) => {
  switch(num) {
    case 0:
      return (<rect width="10" height="10" y="5" fill={filled ? "#DE1C1C" : "#efefef"} stroke="black"/>); // Hankkeen aloitus
    case 1:
      return (<circle cx="6" cy="10" r="5" fill={filled ? "#AAAAAA" : "#efefef"} stroke="black" />) // Lausuntokierroksen asiakirjat(!) - ei tietoa mitä ovat
    case 2:
      return (<circle cx="6" cy="10" r="5" fill={filled ? "#064EEA" : "#efefef"} stroke="black" />) // Lausuntokierroksen lausunnot
    case 3:
      return (<polygon points="6,5 12,15 0,15" fill={filled ? "#FBB901" : "#efefef"} stroke="black"/>) // Lausuntokierroksen muut asiakirjat
    case 4:
      return (<polygon points="3,0 7,0 10,3 10,7 7,10 3,10 0,7 0,3" fill={filled ? "#01953F" : "#efefef"} stroke="black"/>) // Lakialoitteet
    case 5:
      return (<polygon points="5,0 6.2,3.5 10,3.5 7,6 8.2,10 5,7.5 1.8,10 3,6 0,3.5 3.8,3.5" fill={filled ? "#F37500" : "#efefef"} stroke="black" />)  // Kansalaisaloitteet
    case 6:
      return (<rect width="12" height="8" y="5" fill={filled ? "#8724B2" : "#efefef"} stroke="black"/>) // Hallituksen esitykset
    case 7:
      return (<polygon points="8,0 14,5 11,14 3,14 0,5" fill={filled ? "#7A4419" : "#efefef"} stroke="black"/>) // Asiantuntijalausunnot
    case 8:
      return (<path d="M1,5 A8,8 0 0,0 17,5 Z" fill={filled ? "#4CC9F0" : "#efefef"} stroke="black"/>) // Valiokunta-asiakirjat
    case 9:
      return (<path d="M3,5 H7 V8 H10 V12 H7 V15 H3 V12 H0 V8 H3 Z" fill={filled ? "white" : "#efefef"} stroke="black"/>) // Eduskuntakeskustelut
    case 10:
      return (<polygon points="6,15 12,5 0,5" fill={filled ? "#FF85AC" : "#efefef"} stroke="black"/>) // Eduskunnan vastaukset
    case 11:
      return (<polygon points="6,5 12,12 6,19 0,12" fill={filled ? "#000000" : "#efefef"} stroke="black" />) // Lakipäätökset
    default:
      return (<circle cx="6" cy="10" r="5" fill={filled ? "#AAAAAA" : "#efefef"} stroke="black" />);
  }
};